import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import {
  defaultLocale,
  I18NEXT_LOCALE_KEY,
  mappedLocales,
  supportedLocales,
} from './consts';
import LocaleContext from './context';
import { useLocaleProviderStore } from './store';

export const LocaleProvider: React.FC<{
  children?: ReactNode;
  locale?: string;
}> = ({ children, locale }) => {
  const browserLanguage = supportedLocales.find(
    (l) => l === navigator.language,
  );

  const mappedBrowserLanguage = mappedLocales[navigator.language];

  const browserDerivedLocale =
    browserLanguage || mappedBrowserLanguage || defaultLocale;

  const [customLocale, setCustomLocale] = useState(() => {
    try {
      return localStorage.getItem(I18NEXT_LOCALE_KEY) ?? browserDerivedLocale;
    } catch {
      return browserDerivedLocale;
    }
  });
  const [localeSetExplicitly, setLocaleSetExplicitly] = useState(false);
  const effectiveLocale = locale || customLocale;

  const { setLocale: setStoreLocale } = useLocaleProviderStore(effectiveLocale);

  useEffect(() => {
    setStoreLocale(effectiveLocale);
  }, [effectiveLocale, setStoreLocale]);

  // setLocale must be referentially stable so consumers can safely include it
  // in `useEffect` dependency arrays without triggering infinite re-render
  const setLocale = useCallback(
    (newLocale: string) => {
      setCustomLocale(newLocale);
      setLocaleSetExplicitly(true);
      setStoreLocale(newLocale);
    },
    [setStoreLocale],
  );

  useEffect(() => {
    setLocaleSetExplicitly(false);
  }, [locale]);

  const context = useMemo(
    () => ({
      locale: effectiveLocale,
      setLocale,
    }),
    [effectiveLocale, setLocale],
  );

  useEffect(() => {
    document.documentElement.lang = effectiveLocale;
  }, [effectiveLocale]);

  const parentContext = useContext(LocaleContext);
  const isOutermostProvider = parentContext === undefined;

  useEffect(() => {
    if (!isOutermostProvider) return;
    if (!locale && !localeSetExplicitly) return;
    try {
      localStorage.setItem(I18NEXT_LOCALE_KEY, effectiveLocale);
    } catch {
      // localStorage unavailable (private mode, quota exceeded, sandboxed iframe)
    }
  }, [effectiveLocale, isOutermostProvider, locale, localeSetExplicitly]);

  return (
    <LocaleContext.Provider value={context}>{children}</LocaleContext.Provider>
  );
};

LocaleProvider.propTypes = {
  locale: PropTypes.string,
};
